import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Users, Sparkles, Music } from 'lucide-react';

const approaches = [
  {
    icon: Users,
    title: 'Personal Consultation',
    description:
      'Every event begins with a one-on-one consultation. We take the time to understand your vision, preferences, and goals to craft an experience that is authentically yours.',
  },
  {
    icon: Sparkles,
    title: 'Designed From the Brief',
    description:
      'We don\'t work from templates or fixed packages. Every element — creative direction, technical production, and flow — is designed around your specific brief, audience, and objectives.',
  },
  {
    icon: Music,
    title: 'Entertainment by Design',
    description:
      'We curate performers and entertainment to fit the brief — not the other way around. From subtle atmospheric moments to high-impact feature performances, every element serves the event.',
  },
];

export const ApproachSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div ref={ref}>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
            >
              Our Philosophy
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#111111] mb-8"
            >
              The New Creative
              <br />
              <span className="text-[#2A93A6]">Approach</span>
            </motion.h2>

            <div className="space-y-8">
              {approaches.map((approach, index) => (
                <motion.div
                  key={approach.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="group"
                  data-testid={`approach-card-${index}`}
                >
                  <div className="flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#EEF8FB] border border-[#47c3d6]/20 group-hover:border-[#47c3d6]/60 rounded-xl flex items-center justify-center transition-all duration-300">
                      <approach.icon size={22} className="text-[#47c3d6]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#111111] mb-2 group-hover:text-[#17798A] transition-colors">
                        {approach.title}
                      </h3>
                      <p className="text-[#666666] text-sm leading-relaxed">
                        {approach.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
              <img
                src="/brand/approach.jpg"
                alt="NCI corporate awards production"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#47c3d6]/30 -z-10 rounded-xl" />
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t border-l border-[#47c3d6]/40" />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 section-divider" />
    </section>
  );
};
